const GAME_PORTAL_RESUME_STORAGE_KEY = "apk-games:portal-resume";
const GAME_PORTAL_RESUME_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const GAME_PORTAL_RESUME_MAX_JSON_LENGTH = 64 * 1024;

const CALLBACK_QUERY_KEYS = [
  "game_embed",
  "game_return_to",
  "jwttoken",
  "game_state",
  "game_auth",
  "game_action",
  "game_portal_target",
  "package_id",
  "model_id",
  "return_to",
] as const;

interface PendingGamePortalResume {
  packageId: number;
  createdAt: number;
  location: string;
  historyState?: unknown;
  gameState?: unknown;
}

let captureGameState: (() => unknown) | undefined;
let beforePortalLeave: (() => Promise<void>) | undefined;

function readPending(packageId: number) {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.sessionStorage.getItem(GAME_PORTAL_RESUME_STORAGE_KEY);
    if (!raw) return undefined;
    const pending = JSON.parse(raw) as Partial<PendingGamePortalResume>;
    if (pending.packageId !== packageId || !pending.createdAt || Date.now() - pending.createdAt > GAME_PORTAL_RESUME_MAX_AGE_MS) {
      window.sessionStorage.removeItem(GAME_PORTAL_RESUME_STORAGE_KEY);
      return undefined;
    }
    if (typeof pending.location !== "string") {
      window.sessionStorage.removeItem(GAME_PORTAL_RESUME_STORAGE_KEY);
      return undefined;
    }
    return pending as PendingGamePortalResume;
  } catch {
    try {
      window.sessionStorage.removeItem(GAME_PORTAL_RESUME_STORAGE_KEY);
    } catch {
      // 会话存储不可用时继续按普通冷启动处理。
    }
    return undefined;
  }
}

/**
 * 注册游戏自己的短期 UI 现场捕获器，只服务于门户往返。
 * 长期存档不得混入页面、弹窗等临时界面状态。
 */
export function configureGamePortalResumeCapture(capture: () => unknown, beforeLeave?: () => Promise<void>) {
  captureGameState = capture;
  beforePortalLeave = beforeLeave;
  return () => {
    if (captureGameState === capture) { captureGameState = undefined; beforePortalLeave = undefined; }
  };
}

export function flushGameBeforePortalLeave() {
  return beforePortalLeave?.();
}

export function prepareGamePortalResume(packageId: number) {
  if (typeof window === "undefined") return;
  try {
    const current = new URL(window.location.href);
    CALLBACK_QUERY_KEYS.forEach((key) => current.searchParams.delete(key));
    const pending: PendingGamePortalResume = {
      packageId,
      createdAt: Date.now(),
      location: `${current.pathname}${current.search}${current.hash}`,
      historyState: window.history.state,
      gameState: captureGameState?.(),
    };
    const raw = JSON.stringify(pending);
    if (raw.length > GAME_PORTAL_RESUME_MAX_JSON_LENGTH) {
      delete pending.gameState;
    }
    window.sessionStorage.setItem(GAME_PORTAL_RESUME_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // 返回现场属于体验增强；失败时仍允许门户认证与游戏存档正常工作。
  }
}

/** 在 React 和游戏路由初始化前恢复干净的游戏内地址。 */
export function restoreGamePortalReturnLocation(packageId: number) {
  const pending = readPending(packageId);
  if (!pending || typeof window === "undefined") return false;
  try {
    const current = new URL(window.location.href);
    const destination = new URL(pending.location, current.origin);
    if (destination.origin !== current.origin) return false;

    // 官网回调参数必须保留到 SessionStore 完成消费和清理，恢复页面不能绕过 JWT 与 state 校验。
    CALLBACK_QUERY_KEYS.forEach((key) => {
      const value = current.searchParams.get(key);
      if (value) destination.searchParams.set(key, value);
    });
    window.history.replaceState(
      pending.historyState ?? window.history.state,
      "",
      `${destination.pathname}${destination.search}${destination.hash}`,
    );
    return true;
  } catch {
    return false;
  }
}

/** 游戏长期存档完成水合后一次性消费短期 UI 现场。 */
export function consumeGamePortalResume<T = unknown>(packageId: number): T | undefined {
  const pending = readPending(packageId);
  if (!pending || typeof window === "undefined") return undefined;
  try {
    window.sessionStorage.removeItem(GAME_PORTAL_RESUME_STORAGE_KEY);
  } catch {
    // 已读取的现场不会阻塞后续恢复。
  }
  return pending.gameState as T | undefined;
}
